import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TaxonomyExplorer from './TaxonomyExplorer';
import { TaxonomyData, AppAspect, AppTheme, AppRelationship, ID } from './types';
import AutocompleteSearchInput from './components/ui/AutocompleteSearchInput';
import { useTaxonomyStore } from './taxonomyStore';
import { PathObject, cn } from './lib/utils';
import { ThemeToggle } from './components/ui/ThemeToggle';

// Remove the temporary local TaxonomyData interface
// interface TaxonomyData { nodes: any[]; themes: any[]; relationships: any[]; maxLevel: number; identifier: string; }


// Simplified in-memory storage and data fetching for this minimal version
const inMemoryTaxonomies = new Map<string, { data: any, name: string }>();

const handleTaxonomyUploadMinimal = async (file: File, taxonomyName: string): Promise<{ success: boolean, taxonomyFolder?: string, displayName?: string, message: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const fileContent = e.target?.result as string;
                const taxData = JSON.parse(fileContent);
                const uniqueId = `memory_${Date.now()}`;
                inMemoryTaxonomies.set(uniqueId, { data: taxData, name: taxonomyName });
                console.log('[App.tsx] File uploaded to memory:', uniqueId, taxData);
                resolve({ success: true, taxonomyFolder: uniqueId, displayName: taxonomyName, message: 'Uploaded' });
            } catch (err: any) {
                console.error('[App.tsx] JSON Parse Error:', err);
                reject(new Error('Invalid JSON: ' + err.message));
            }
        };
        reader.onerror = (err) => {
            console.error('[App.tsx] File Read Error:', err);
            reject(new Error('Error reading file'));
        }
        reader.readAsText(file);
    });
};

const fetchTaxonomyDataMinimal = async (taxonomyFolder: string): Promise<TaxonomyData | null> => {
    console.log('[App.tsx] Fetching data for:', taxonomyFolder);
    if (taxonomyFolder.startsWith('memory_')) {
        const stored = inMemoryTaxonomies.get(taxonomyFolder);
        if (stored && stored.data?.aspects) {
            const rawData = stored.data;
            const dataRoot = rawData;

            const org_slug = rawData.org_slug || rawData.identifier || taxonomyFolder;
            const identifier = org_slug;

            const originalNodesInput: any[] = dataRoot.aspects;
            let aspectNodes: AppAspect[] = [];
            let themeObjects: AppTheme[] = [];
            let finalRelationships: AppRelationship[] = [];

            originalNodesInput.forEach((inputAspect: any) => {
                const aspectData = { ...inputAspect, themeCount: 0 };
                const currentThemesForAspect = aspectData.themes; delete aspectData.themes;
                aspectNodes.push(aspectData);
                if (currentThemesForAspect && Array.isArray(currentThemesForAspect)) {
                    aspectData.themeCount = currentThemesForAspect.length;
                    currentThemesForAspect.forEach((themeInput: any) => {
                        const subThemes = (themeInput.sub_themes && Array.isArray(themeInput.sub_themes))
                            ? themeInput.sub_themes.map((stInput: any) => {
                                const subThemeData: any = typeof stInput === 'string' ? { name: stInput } : { ...stInput };
                                if (org_slug && stInput.citations && Array.isArray(stInput.citations)) {
                                    subThemeData.citations = stInput.citations.map((cit: any) => ({
                                        feedback_id: cit.feedback_id,
                                        url: `https://dashboard.enterpret.com/${org_slug}/record/${cit.feedback_id}`
                                    }));
                                }
                                return subThemeData;
                            }) : [];

                        const themeData: AppTheme = {
                            ...themeInput,
                            // Handle both old and new formats
                            parentAspectName: themeInput.parent_aspect_name || aspectData.name,
                            parent_aspect_name: themeInput.parent_aspect_name,
                            grandparent_aspect_name: themeInput.grandparent_aspect_name,
                            parentAspectLevel: aspectData.level,
                            type: 'theme',
                            sub_themes: subThemes,
                            citations: [] // Initialize, will populate next
                        };

                        if (org_slug && themeInput.citations && Array.isArray(themeInput.citations)) {
                            themeData.citations = themeInput.citations.map((cit: any) => ({
                                feedback_id: cit.feedback_id,
                                url: `https://dashboard.enterpret.com/${org_slug}/record/${cit.feedback_id}`
                            }));
                        }

                        themeObjects.push(themeData); finalRelationships.push({ parent_id: aspectData.name, child_id: themeData.name, type: 'aspect_to_theme' });
                    });
                }
            });

            // Process root-level themes if they exist (new format)
            if (dataRoot.themes && Array.isArray(dataRoot.themes)) {
                dataRoot.themes.forEach((themeInput: any) => {
                    const subThemes = (themeInput.sub_themes && Array.isArray(themeInput.sub_themes))
                        ? themeInput.sub_themes.map((stInput: any) => {
                            const subThemeData: any = typeof stInput === 'string' ? { name: stInput } : { ...stInput };
                            if (org_slug && stInput.citations && Array.isArray(stInput.citations)) {
                                subThemeData.citations = stInput.citations.map((cit: any) => ({
                                    feedback_id: cit.feedback_id,
                                    url: `https://dashboard.enterpret.com/${org_slug}/record/${cit.feedback_id}`
                                }));
                            }
                            return subThemeData;
                        }) : [];

                    const themeData: AppTheme = {
                        ...themeInput,
                        // For root-level themes, use the provided parent_aspect_name
                        parentAspectName: themeInput.parent_aspect_name,
                        parent_aspect_name: themeInput.parent_aspect_name,
                        grandparent_aspect_name: themeInput.grandparent_aspect_name,
                        parentAspectLevel: undefined, // Will be determined later if needed
                        type: 'theme',
                        sub_themes: subThemes,
                        citations: [] // Initialize, will populate next
                    };

                    if (org_slug && themeInput.citations && Array.isArray(themeInput.citations)) {
                        themeData.citations = themeInput.citations.map((cit: any) => ({
                            feedback_id: cit.feedback_id,
                            url: `https://dashboard.enterpret.com/${org_slug}/record/${cit.feedback_id}`
                        }));
                    }

                    themeObjects.push(themeData);
                });
            }

            // Process relationships - either from relationships array OR generate from parent_aspect_name fields
            if (dataRoot.relationships && Array.isArray(dataRoot.relationships)) {
                dataRoot.relationships.forEach((rel: any) => finalRelationships.push({ ...rel, type: 'aspect_to_aspect' }));
            } else {
                // Generate relationships from parent_aspect_name fields in aspects
                aspectNodes.forEach((aspect: any) => {
                    if (aspect.parent_aspect_name) {
                        finalRelationships.push({
                            parent_id: aspect.parent_aspect_name,
                            child_id: aspect.name,
                            type: 'aspect_to_aspect'
                        });
                    }
                });
            }

            aspectNodes = aspectNodes.map(node => ({ ...node, name: node.name || 'Unnamed Node', level: node.level ? (String(node.level).startsWith('L') || isNaN(parseInt(String(node.level))) ? String(node.level) : `L${node.level}`) : 'L1', themeCount: node.themeCount === undefined ? 0 : node.themeCount })) as AppAspect[];
            const processedData: TaxonomyData = { nodes: aspectNodes, themes: themeObjects, relationships: finalRelationships, maxLevel: Math.max(0, ...aspectNodes.map(node => parseInt(String(node.level).replace('L', '')) || 0)), identifier, org_slug };
            console.log('[App.tsx] Processed taxonomy data:', processedData);
            return processedData;
        }
        console.warn('[App.tsx] Stored data malformed or missing root .aspects array:', stored);
    }
    return null;
};

const App: React.FC = () => {
    const [taxonomyData, setTaxonomyData] = useState<TaxonomyData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentTaxonomyId, setCurrentTaxonomyId] = useState<string | null>(null);
    const { path, setL1, setL2, setL3, setTheme } = useTaxonomyStore();

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            console.log('[App.tsx] File selected:', file.name);
            setIsLoading(true); setError(null); setTaxonomyData(null); setCurrentTaxonomyId(null);
            setL1(undefined);
            try {
                const result = await handleTaxonomyUploadMinimal(file, file.name.replace('.json', ''));
                console.log('[App.tsx] Upload result:', result);
                if (result.success && result.taxonomyFolder) {
                    setCurrentTaxonomyId(result.taxonomyFolder);
                }
            } catch (e: any) {
                console.error('[App.tsx] Upload error:', e);
                setError(e.message);
            }
            setIsLoading(false);
        }
    };

    useEffect(() => {
        console.log('[App.tsx] useEffect for currentTaxonomyId:', currentTaxonomyId);
        if (currentTaxonomyId) {
            setIsLoading(true); setError(null);
            fetchTaxonomyDataMinimal(currentTaxonomyId)
                .then(data => {
                    console.log('[App.tsx] Data fetched for ' + currentTaxonomyId + ':', data);
                    setTaxonomyData(data);
                    if (!data) {
                        console.warn('[App.tsx] Setting error: Failed to process or load taxonomy data for ' + currentTaxonomyId);
                        setError('Failed to process or load taxonomy data for ' + currentTaxonomyId);
                    }
                })
                .catch(e => {
                    console.error('[App.tsx] Fetch/process error:', e);
                    setError(e.message);
                })
                .finally(() => {
                    console.log('[App.tsx] Fetch complete, setIsLoading(false)');
                    setIsLoading(false);
                });
        }
    }, [currentTaxonomyId]);

    console.log('[App.tsx] Rendering. isLoading:', isLoading, 'error:', error, 'taxonomyData available:', !!taxonomyData);

    const handlePathSelect = useCallback((selectedPath: PathObject) => {
        if (path.l1 !== selectedPath.l1) setL1(selectedPath.l1);
        if (path.l2 !== selectedPath.l2) setL2(selectedPath.l2);
        if (path.l3 !== selectedPath.l3) setL3(selectedPath.l3);
    }, [setL1, setL2, setL3, path.l1, path.l2, path.l3]);

    const handlePathClear = useCallback(() => {
        setL1(undefined);
    }, [setL1]);

    const displayTaxonomyData = useMemo(() => {
        return taxonomyData;
    }, [taxonomyData]);

    return (
        <div className="flex flex-col h-screen bg-gradient-to-br from-background-tertiary to-white dark:from-gray-900 dark:to-gray-800">
            <header className="px-5 py-3 border-b border-slate-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-between gap-4 shadow-sm sticky top-0 z-40 transition-all duration-200">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-semibold tracking-tight text-primary dark:text-gray-100">Taxonomy Playground</h1>

                    <label
                        className={cn(
                            "relative inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium",
                            "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-200",
                            "cursor-pointer border border-blue-200 dark:border-blue-800",
                            isLoading && "opacity-50 cursor-not-allowed"
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                        </svg>
                        Choose File
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleFileUpload}
                            disabled={isLoading}
                            className="sr-only"
                        />
                    </label>

                    {currentTaxonomyId && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-md">
                            <span className="font-medium mr-1 text-gray-600 dark:text-gray-300">ID:</span>
                            <code className="text-gray-500 dark:text-gray-400 font-mono text-xs">{currentTaxonomyId}</code>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-4">
                    {taxonomyData && taxonomyData.nodes && taxonomyData.relationships && (
                        <AutocompleteSearchInput
                            allNodes={taxonomyData.nodes}
                            allRelationships={taxonomyData.relationships}
                            onPathSelect={handlePathSelect}
                            onPathClear={handlePathClear}
                            placeholder="Search and select aspect..."
                            allOriginalNodesForLabel={taxonomyData.nodes}
                            allOriginalRelationshipsForLabel={taxonomyData.relationships}
                        />
                    )}

                    <ThemeToggle />
                </div>
            </header>

            <main className="flex-grow overflow-auto relative">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-10">
                        <div className="flex flex-col items-center">
                            <div className="h-8 w-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin mb-3"></div>
                            <p className="text-sm text-gray-600 dark:text-gray-300">Loading taxonomy data...</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="p-4 m-4 text-center text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow-sm">
                        <div className="flex items-center justify-center mb-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="font-medium">Error</span>
                        </div>
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {!isLoading && !error && displayTaxonomyData && (
                    <TaxonomyExplorer
                        taxonomyData={displayTaxonomyData}
                        taxonomyIdentifier={currentTaxonomyId || 'unknown'}
                    />
                )}

                {!isLoading && !error && displayTaxonomyData && displayTaxonomyData.nodes.length === 0 && currentTaxonomyId && (
                    <div className="p-8 m-4 text-center bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-400 dark:text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <p className="text-gray-600 dark:text-gray-300">No aspects found in the loaded taxonomy.</p>
                    </div>
                )}

                {!isLoading && !error && !displayTaxonomyData && currentTaxonomyId && (
                    <div className="p-8 m-4 text-center bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-orange-400 dark:text-orange-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-orange-600 dark:text-orange-400 font-medium mb-1">Invalid Taxonomy Data</p>
                        <p className="text-gray-600 dark:text-gray-300 text-sm">Ensure the JSON is valid and contains a root level 'aspects' array.</p>
                    </div>
                )}

                {!isLoading && !error && !currentTaxonomyId && (
                    <div className="flex flex-col items-center justify-center h-full p-4">
                        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div className="p-5 bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-white">
                                <h2 className="text-xl font-semibold tracking-tight mb-1">Welcome to Taxonomy Playground</h2>
                                <p className="text-blue-100 dark:text-blue-200 text-sm">Explore and visualize taxonomy hierarchies with ease</p>
                            </div>

                            <div className="p-6">
                                <div className="flex flex-col items-center text-center mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-100 dark:text-blue-900 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <p className="text-gray-600 dark:text-gray-300 mb-3">Upload a JSON file to get started with your taxonomy exploration.</p>

                                    <label className="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors duration-200 cursor-pointer w-full">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        Upload Taxonomy JSON
                                        <input type="file" accept=".json" onChange={handleFileUpload} disabled={isLoading} className="sr-only" />
                                    </label>
                                </div>

                                <div className="text-xs text-gray-500 dark:text-gray-400 border-t dark:border-gray-700 pt-4">
                                    <p className="mb-1 font-medium text-gray-600 dark:text-gray-300">Expected JSON format:</p>
                                    <code className="block bg-gray-50 dark:bg-gray-900 p-2 rounded text-xs font-mono overflow-auto whitespace-pre">
                                        {`{
  "org_slug": "your_org_slug",
  "aspects": [
    { "name": "L1 Aspect", "level": "L1", "themes": [...], ... },
    ...
  ],
  "relationships": [...]
}`}
                                    </code>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default App;
